/**
 * Higher-level session helpers built on top of WbAgentClient.
 *
 * This module provides turn-level abstractions (message threading, parent
 * resolution) while keeping zero React/UI dependencies so it can be consumed
 * by any frontend framework.
 *
 * Step extraction lives in WbAgentSteps — re-exported here for convenience.
 */

import {
  TERMINAL_STATES,
  type TurnResponse,
  type TurnState,
  type WbAgentClient,
} from './WbAgentClient';
import {type ProgressStep, extractSteps} from './WbAgentSteps';

// Re-export step-level API so existing consumers don't need two imports
export {extractSteps, summarizeToolArgs} from './WbAgentSteps';
export type {ProgressStep} from './WbAgentSteps';

// =============================================================================
// Constants
// =============================================================================

export const STATE_LABELS: Record<TurnState, string> = {
  queued: 'Queued',
  in_progress: 'Running',
  completed: 'Completed',
  errored: 'Error',
  cancelled: 'Cancelled',
};

// =============================================================================
// Types
// =============================================================================

export type ChatMessage =
  | {role: 'user'; text: string}
  | {role: 'agent'; text: string}
  | {role: 'steps'; steps: ProgressStep[]}
  | {role: 'loading'; steps: ProgressStep[]};

export type Tab = 'messages' | 'history';

// =============================================================================
// Turn threading
// =============================================================================

/** Convert a list of ordered turns into ChatMessage entries. */
export function turnsToMessages(turns: TurnResponse[]): {
  messages: ChatMessage[];
  activeTurnId: string | null;
} {
  const messages: ChatMessage[] = [];
  let activeTurnId: string | null = null;
  for (const turn of turns) {
    messages.push({role: 'user', text: turn.user_prompt});
    const steps = extractSteps(turn);
    if (turn.final_output) {
      if (steps.length > 0) {
        messages.push({role: 'steps', steps});
      }
      messages.push({role: 'agent', text: turn.final_output});
    } else if (!TERMINAL_STATES.includes(turn.state)) {
      messages.push({role: 'loading', steps});
      activeTurnId = turn.id;
    } else if (turn.state === 'errored') {
      messages.push({role: 'agent', text: 'Error'});
    }
  }
  return {messages, activeTurnId};
}

/** Strip trailing cancelled/errored exchanges from a message list. */
export function stripFailedTail(messages: ChatMessage[]): ChatMessage[] {
  const trimmed = [...messages];
  while (trimmed.length > 0) {
    const last = trimmed[trimmed.length - 1];
    if (
      last.role === 'agent' &&
      (last.text === 'Cancelled.' || last.text === 'Error')
    ) {
      trimmed.pop();
      if (trimmed.length > 0 && trimmed[trimmed.length - 1].role === 'steps') {
        trimmed.pop();
      }
      if (trimmed.length > 0 && trimmed[trimmed.length - 1].role === 'user') {
        trimmed.pop();
      }
    } else {
      break;
    }
  }
  return trimmed;
}

/**
 * Find the best parent turn for a new message in a conversation.
 * Walks back from the leaf, skipping cancelled/errored turns so the new
 * turn effectively replaces the failed one.
 */
export async function getParentTurnId(
  client: WbAgentClient,
  rootTurnId: string
): Promise<string> {
  const turns = await client.get('/turns', {
    root_turn_id: rootTurnId,
    sort_by: 'created_at',
    sort_desc: false,
  });
  if (turns.length === 0) {
    return rootTurnId;
  }
  const parentIds = new Set(
    turns.map((t: TurnResponse) => t.parent_turn_id).filter(Boolean)
  );
  const leaves = turns.filter((t: TurnResponse) => !parentIds.has(t.id));
  let leaf =
    leaves.length > 0 ? leaves[leaves.length - 1] : turns[turns.length - 1];

  const turnById = new Map(turns.map((t: TurnResponse) => [t.id, t]));
  while (
    leaf &&
    (leaf.state === 'cancelled' || leaf.state === 'errored') &&
    leaf.parent_turn_id
  ) {
    const parent = turnById.get(leaf.parent_turn_id);
    if (!parent) {
      break;
    }
    leaf = parent;
  }

  return leaf.id;
}

/**
 * Find the root_turn_id for the most recently active conversation.
 * Uses the most recent turn (of any kind) and returns its root.
 */
export async function getMostRecentSessionId(
  client: WbAgentClient
): Promise<string | null> {
  const mostRecent = await client
    .get('/turns', {sort_by: 'created_at', sort_desc: true})
    .catch(() => [] as TurnResponse[]);
  return mostRecent.length > 0 ? mostRecent[0].root_turn_id : null;
}
